import { AddCardV2Icon } from "lola-framework-ui-test";
import { PayoutInfo } from "./PayoutInfo";
import { useStore } from "../../../../store/globalState";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES, flavorClassName } from "../../../../constants";
import { paymentType } from "../../../../interfaces/payment";
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../store/useSessionStore";
import { getAllPaymentMethods } from "../../../../services/api";
import type { ICardPaymentMethod } from "../../../../interfaces/payment-method";
import masterCardLogo from "../../../../assets/master_card_icon.png";
import visaLogo from "../../../../assets/visa_color_icon.png";
import { useRouteWithToken } from "../../../../hooks/useRouteWithToken";

export interface SelectPaymentMethodSlotProps {
  onClose: () => void;
  fee?: number;
}

export const SelectPaymentMethodSlot: React.FC<
  SelectPaymentMethodSlotProps
> = ({ onClose, fee }) => {
  const { t } = useTranslation();
  const { theme, setPaymentState, cardList, exchangeRate } = useStore();
  const { session } = useSessionStore();
  const colorConfig = theme.whiteView;
  const { footerColor, backgroundBtn } = colorConfig;
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();

  const [cards, setCards] = useState<ICardPaymentMethod[]>([]);

  useEffect(() => {
    const linkId = session.data?.linkId;
    if (!linkId) return;

    getAllPaymentMethods(linkId)
      .then((methods) => {
        setCards(methods.cards.filter((c) => c.enabled));
      })
      .catch((err) => {
        console.error("Failed to fetch payment methods:", err);
        navigate(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
      });
  }, [session.data?.linkId, cardList, navigate]);

  const onSelectPayment = ({
    cardId,
    type,
    brand,
  }: {
    cardId: string | null;
    type: paymentType;
    brand: string;
  }) => {
    setPaymentState({
      payment: {
        cardId,
        type,
        brand,
      },
    });
    onClose();
  };

  const pickIcon = useCallback(
    (brand: string) => {
      let finalIcon = null;
      if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
      if (brand.toLocaleUpperCase().includes("MASTERCARD"))
        finalIcon = masterCardLogo;

      return {
        brand,
        icon: (
          <span
            className="icon-wrapper"
            style={{ "--bgr": backgroundBtn } as CSSProperties}
          >
            <img
              className="icon-flavor-c"
              src={finalIcon ?? ""}
              alt={`${brand} logo`}
            />
          </span>
        ),
      };
    },
    [backgroundBtn]
  );

  return (
    <section
      className={`select-payer-slot ${flavorClassName}`}
    >
      <h2 className="foreground text-centered h2">
        {t("payment.selectMethod")}
      </h2>
      <section className="select-payer-slot-container">
        {cards.map((card) => {
          const displayName = `${card.brand}-${card.last4}`;
          return (
            <PayoutInfo
              key={card.paymentMethodId}
              title={displayName}
              subtitle={t("payment.minMax", {
                min: exchangeRate?.lowerLimit ?? "0",
                max: exchangeRate?.upperLimit ?? "0",
              })}
              icon={pickIcon(card.brand).icon}
              fee={
                <label className="right-text muted-foreground destination-info-text">
                  {t("payment.fee", { amount: fee })}
                </label>
              }
              onChange={() =>
                onSelectPayment({
                  cardId: card.paymentMethodId,
                  type: paymentType.CARD,
                  brand: displayName,
                })
              }
            />
          );
        })}
        <span className="divisor" />
        <PayoutInfo
          title={t("payment.addDebitCard")}
          subtitle={t("payment.minMax", {
            min: exchangeRate?.lowerLimit ?? "0",
            max: exchangeRate?.upperLimit ?? "0",
          })}
          icon={
            <span
              className="icon-wrapper"
              style={{ "--bgr": backgroundBtn } as CSSProperties}
            >
              <AddCardV2Icon
                size={32}
                className="flavor-c-icon"
                colors={[footerColor, footerColor]}
              />
            </span>
          }
          fee={
            <label className="right-text muted-foreground destination-info-text">
              {t("payment.fee", { amount: fee })}
            </label>
          }
          onChange={() => redirectTo(APP_ROUTES.ADD_CARD_PAGE)}
        />
      </section>
    </section>
  );
};
